import React from 'react'

export interface Comment {
  id: string
  author: {
    id: string
    name: string
    avatar?: string
  }
  content: string
  createdAt: string
  mentions?: string[]
  likes?: string[] // Array of user IDs who liked
  parentId?: string | null // For threaded replies
  replies?: Comment[] // Nested comments
}

interface CommentListProps {
  comments: Comment[]
  currentUserId?: string
  onLike?: (commentId: string) => void
  onReply?: (commentId: string, authorName: string) => void
}

const Avatar = ({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'
  return (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 font-semibold text-black`}
    >
      {initials}
    </div>
  )
}

const HeartIcon = ({ filled, className = '' }: { filled: boolean; className?: string }) =>
  filled ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ) : (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  )

interface CommentItemProps {
  comment: Comment
  currentUserId?: string
  onLike?: (commentId: string) => void
  onReply?: (commentId: string, authorName: string) => void
  depth?: number
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUserId = 'user-1',
  onLike,
  onReply,
  depth = 0,
}) => {
  const isLiked = comment.likes?.includes(currentUserId) || false
  const likesCount = comment.likes?.length || 0

  return (
    <div className={`group flex gap-3 transition-all duration-200 ${depth > 0 ? 'ml-10' : ''}`}>
      <Avatar name={comment.author.name} size="md" />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-sm font-semibold text-white transition-colors group-hover:text-amber-400">
            {comment.author.name}
          </span>
          <span className="text-[11px] text-gray-500">• {comment.createdAt}</span>
        </div>

        <div className="rounded-2xl bg-gray-800/40 px-4 py-3 transition-all group-hover:bg-gray-800/60">
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-300">
            {comment.content.split(/(@\w+\s\w+)/g).map((part, i) => {
              if (part.startsWith('@')) {
                return (
                  <span
                    key={i}
                    className="cursor-pointer font-medium text-amber-400 hover:underline"
                  >
                    {part}
                  </span>
                )
              }
              return part
            })}
          </p>
        </div>

        <div className="mt-2 flex items-center gap-4 px-1">
          <button
            onClick={() => onReply?.(comment.id, comment.author.name)}
            className="text-[11px] font-medium text-gray-500 transition-colors hover:text-amber-400"
          >
            Odpowiedz
          </button>

          <button
            onClick={() => onLike?.(comment.id)}
            className={`group/like flex items-center gap-1.5 transition-all duration-200 ${
              isLiked ? 'text-amber-400' : 'text-gray-500 hover:text-amber-400'
            }`}
          >
            <HeartIcon
              filled={isLiked}
              className={`transition-transform duration-200 ${isLiked ? 'scale-110' : 'group-hover/like:scale-110'}`}
            />
            {likesCount > 0 && <span className="text-[11px] font-medium">{likesCount}</span>}
          </button>
        </div>

        {/* Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-4 space-y-4 pl-4">
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                currentUserId={currentUserId}
                onLike={onLike}
                onReply={onReply}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export const CommentList: React.FC<CommentListProps> = ({
  comments,
  currentUserId,
  onLike,
  onReply,
}) => {
  if (comments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-800/50">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-gray-500"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <h4 className="text-sm font-medium text-gray-300">Brak komentarzy</h4>
        <p className="mt-1 text-xs text-gray-500">Bądź pierwszym, który skomentuje to zadanie.</p>
      </div>
    )
  }

  // Build comment tree from flat array
  const buildTree = (flatComments: Comment[]) => {
    const commentMap: Record<string, Comment & { replies: Comment[] }> = {}
    const tree: Comment[] = []

    flatComments.forEach((comment) => {
      commentMap[comment.id] = { ...comment, replies: [] }
    })

    flatComments.forEach((comment) => {
      const mappedComment = commentMap[comment.id]
      if (comment.parentId && commentMap[comment.parentId]) {
        commentMap[comment.parentId].replies.push(mappedComment)
      } else {
        tree.push(mappedComment)
      }
    })

    return tree
  }

  const commentTree = buildTree(comments)

  return (
    <div className="space-y-6 pb-20">
      {commentTree.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          currentUserId={currentUserId}
          onLike={onLike}
          onReply={onReply}
        />
      ))}
    </div>
  )
}
